/* eslint-disable */
// TEMPORARY visual mockup used ONLY to let the user choose a chat layout.
// Not wired to any backend. Renders faux data with the real .notes-scope theme.
// Route: /chat-preview?opt=a|b|c&theme=light|dark&view=desktop|mobile
import React from 'react';
import {
  MessageSquare, NotebookPen, ListChecks, Search, VolumeX, Users, Plus,
  Check, CheckCheck, ChevronLeft, MoreVertical, Send, Paperclip, Feather,
  Settings2, ArrowLeft, Smile,
} from 'lucide-react';

const useQuery = () => {
  const p = new URLSearchParams(window.location.search);
  return {
    opt: (p.get('opt') || 'a').toLowerCase(),
    theme: (p.get('theme') || 'dark').toLowerCase(),
    view: (p.get('view') || 'desktop').toLowerCase(),
  };
};

// ---- faux data -------------------------------------------------------------
const PEOPLE = [
  { id: '1', name: 'Alice Kapoor', online: true },
  { id: '2', name: 'Bob Menon', online: true },
  { id: '3', name: 'Priya Shah', online: false, seen: '2h ago' },
  { id: '4', name: 'Vibha Rao', online: true },
  { id: '5', name: 'Sam Ali', online: false, seen: 'yesterday' },
  { id: '6', name: 'Neha Iyer', online: false, seen: '3d ago' },
];
const CONVS = [
  { id: 'c1', name: 'Alice Kapoor', online: true, preview: 'Can we ship the update today?', time: '4:02 PM', unread: 3, active: true },
  { id: 'c2', name: 'Design Team', group: true, preview: 'Vibha: Let’s lock the palette 🎨', time: '3:41 PM', unread: 0, members: 5 },
  { id: 'c3', name: 'Bob Menon', online: true, preview: 'Thanks, that works!', time: '1:15 PM', unread: 0, muted: true },
  { id: 'c4', name: 'Priya Shah', online: false, preview: 'Sent you the report card', time: 'Tue', unread: 1 },
  { id: 'c5', name: 'Ops', group: true, preview: 'You: Weekly audit done ✅', time: 'Mon', unread: 0, members: 8 },
];
// mine=true → right side; read=green border, unread=red border
const MSGS = [
  { id: 'm1', mine: false, text: 'Hey! Did you get a chance to review the Q3 draft?', time: '3:58 PM', read: true },
  { id: 'm2', mine: true, text: 'Yes — looks great. Just a couple of tweaks on slide 4.', time: '3:59 PM', read: true, seen: true },
  { id: 'm3', mine: false, text: 'Can we ship the update today?', time: '4:02 PM', read: false },
  { id: 'm4', mine: true, text: 'On it — deploying to staging now, will confirm in 10.', time: '4:03 PM', read: false, seen: false },
];

const hue = (id) => ['#6C8FD1', '#C77D57', '#6FA8A0', '#B072B0', '#B8892E', '#7C9A5B'][Number(id) % 6];

const Avatar = ({ name, id, size = 36, group }) => (
  <span className="relative inline-grid shrink-0 place-items-center rounded-full font-bold text-white"
    style={{ width: size, height: size, background: hue(id || '1'), fontSize: size * 0.4 }}>
    {group ? <Users size={size * 0.5} /> : (name || 'U').charAt(0).toUpperCase()}
  </span>
);
const Dot = ({ online }) => (
  <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2"
    style={{ background: online ? 'var(--n-success)' : 'var(--n-muted-2)', borderColor: 'var(--n-surface)' }} />
);
const Badge = ({ n }) => n > 0 ? (
  <span className="ml-auto grid h-5 min-w-5 place-items-center rounded-full px-1.5 text-[11px] font-bold text-white" style={{ background: 'var(--n-accent)' }}>{n}</span>
) : null;

const SectionLabel = ({ children }) => (
  <div className="px-3 pb-1 pt-3 text-[10.5px] font-semibold uppercase tracking-[0.14em]" style={{ color: 'var(--n-muted-2)' }}>{children}</div>
);

const PresenceChip = ({ p }) => (
  <button className="flex shrink-0 flex-col items-center gap-1" style={{ width: 60 }}>
    <span className="relative"><Avatar name={p.name} id={p.id} size={44} /><Dot online={p.online} /></span>
    <span className="w-full truncate text-center text-[11px]" style={{ color: 'var(--n-ink-soft)' }}>{p.name.split(' ')[0]}</span>
  </button>
);

const ConvRow = ({ c }) => (
  <div className={`flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 transition-colors`}
    style={{ background: c.active ? 'var(--n-accent-bg)' : 'transparent' }}>
    <span className="relative"><Avatar name={c.name} id={c.id} group={c.group} /><Dot online={c.online} /></span>
    <span className="min-w-0 flex-1">
      <span className="flex items-center gap-1.5">
        <span className="truncate text-[13.5px] font-semibold" style={{ color: 'var(--n-ink)' }}>{c.name}</span>
        {c.muted && <VolumeX size={12} style={{ color: 'var(--n-muted-2)' }} />}
        <span className="ml-auto shrink-0 text-[11px] tabular-nums" style={{ color: 'var(--n-muted)' }}>{c.time}</span>
      </span>
      <span className="mt-0.5 flex items-center gap-1.5">
        <span className="truncate text-[12px]" style={{ color: c.unread ? 'var(--n-ink-soft)' : 'var(--n-muted)' }}>{c.preview}</span>
        <Badge n={c.unread} />
      </span>
    </span>
  </div>
);

const Ticks = ({ seen }) => (
  seen ? <CheckCheck size={14} style={{ color: 'var(--n-success)' }} /> : <Check size={14} style={{ color: 'var(--n-muted-2)' }} />
);

const Bubble = ({ m }) => {
  const borderColor = m.read ? 'var(--n-success)' : 'var(--n-danger)';
  return (
    <div className={`flex w-full ${m.mine ? 'justify-end' : 'justify-start'}`}>
      <div className="max-w-[78%]">
        {!m.mine && <span className="mb-0.5 ml-1 block text-[11px] font-medium" style={{ color: 'var(--n-muted)' }}>Alice</span>}
        <div className="rounded-2xl border px-3 py-2 text-[13.5px] leading-relaxed"
          style={{
            borderColor,
            background: m.mine ? 'var(--n-accent-bg)' : 'var(--n-surface-2)',
            color: 'var(--n-ink)',
            borderTopRightRadius: m.mine ? 4 : 16,
            borderTopLeftRadius: m.mine ? 16 : 4,
          }}>
          {m.text}
          <span className="mt-1 flex items-center justify-end gap-1 text-[10.5px] tabular-nums" style={{ color: 'var(--n-muted)' }}>
            {m.time}{m.mine && <Ticks seen={m.seen} />}
          </span>
        </div>
      </div>
    </div>
  );
};

const IconBtn = ({ children, active }) => (
  <span className="grid h-9 w-9 place-items-center rounded-lg" style={{ color: active ? 'var(--n-accent)' : 'var(--n-muted)', background: active ? 'var(--n-accent-bg)' : 'transparent' }}>{children}</span>
);

const Composer = () => (
  <div className="flex items-center gap-2 border-t px-3 py-2.5" style={{ borderColor: 'var(--n-line)' }}>
    <IconBtn><Paperclip size={18} /></IconBtn>
    <div className="flex flex-1 items-center gap-2 rounded-xl border px-3 py-2" style={{ borderColor: 'var(--n-border)', background: 'var(--n-surface-2)' }}>
      <input className="flex-1 bg-transparent text-[13.5px] outline-none" style={{ color: 'var(--n-ink)' }} placeholder="Message…" readOnly />
      <Smile size={17} style={{ color: 'var(--n-muted-2)' }} />
    </div>
    <span className="grid h-10 w-10 place-items-center rounded-xl text-white" style={{ background: 'var(--n-accent)' }}><Send size={17} /></span>
  </div>
);

const ThreadHeader = ({ withBack }) => (
  <div className="flex items-center gap-2.5 border-b px-3 py-2.5" style={{ borderColor: 'var(--n-line)' }}>
    {withBack && <ChevronLeft size={20} style={{ color: 'var(--n-muted)' }} />}
    <span className="relative"><Avatar name="Alice Kapoor" id="1" size={34} /><Dot online /></span>
    <span className="min-w-0">
      <span className="block truncate text-[14px] font-semibold" style={{ color: 'var(--n-ink)' }}>Alice Kapoor</span>
      <span className="block text-[11px]" style={{ color: 'var(--n-success)' }}>online</span>
    </span>
    <span className="ml-auto flex items-center gap-0.5">
      <IconBtn><Search size={17} /></IconBtn>
      <IconBtn><MoreVertical size={17} /></IconBtn>
    </span>
  </div>
);

const PanelHeader = ({ title = 'Chat' }) => (
  <div className="flex items-center gap-2 border-b px-3 py-3" style={{ borderColor: 'var(--n-line)' }}>
    <MessageSquare size={18} style={{ color: 'var(--n-accent)' }} />
    <span className="font-display text-[16px] font-semibold" style={{ color: 'var(--n-ink)' }}>{title}</span>
    <span className="ml-auto flex items-center gap-0.5">
      <IconBtn><Search size={17} /></IconBtn>
      <IconBtn><VolumeX size={17} /></IconBtn>
      <span className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12.5px] font-semibold text-white" style={{ background: 'var(--n-accent)' }}><Plus size={14} /> Group</span>
    </span>
  </div>
);

const Thread = () => (
  <div className="flex min-h-0 flex-1 flex-col">
    <ThreadHeader />
    <div className="flex flex-1 flex-col gap-2.5 overflow-y-auto p-3" style={{ background: 'var(--n-canvas)' }}>
      {MSGS.map((m) => <Bubble key={m.id} m={m} />)}
    </div>
    <Composer />
  </div>
);

// ---- Option A: Split Inbox -------------------------------------------------
const OptionA = () => (
  <div className="flex h-full flex-col">
    <PanelHeader />
    <div className="border-b px-3 py-2.5" style={{ borderColor: 'var(--n-line)' }}>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em]" style={{ color: 'var(--n-muted-2)' }}>People</span>
        <Users size={13} style={{ color: 'var(--n-muted)' }} />
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1">{PEOPLE.map((p) => <PresenceChip key={p.id} p={p} />)}</div>
    </div>
    <div className="flex min-h-0 flex-1">
      <div className="w-[44%] shrink-0 overflow-y-auto border-r py-1" style={{ borderColor: 'var(--n-line)' }}>
        <SectionLabel>Conversations</SectionLabel>
        <div className="px-1">{CONVS.map((c) => <ConvRow key={c.id} c={c} />)}</div>
      </div>
      <div className="flex min-w-0 flex-1 flex-col"><Thread /></div>
    </div>
  </div>
);

// ---- Option B: Directory-first (two columns) -------------------------------
const OptionB = () => (
  <div className="flex h-full flex-col">
    <PanelHeader />
    <div className="flex min-h-0 flex-1">
      <div className="w-[38%] shrink-0 overflow-y-auto border-r" style={{ borderColor: 'var(--n-line)' }}>
        <div className="p-2"><div className="flex items-center gap-2 rounded-lg border px-2.5 py-1.5" style={{ borderColor: 'var(--n-border)' }}><Search size={14} style={{ color: 'var(--n-muted-2)' }} /><span className="text-[12px]" style={{ color: 'var(--n-muted-2)' }}>Search people…</span></div></div>
        <SectionLabel>People</SectionLabel>
        {PEOPLE.map((p) => (
          <div key={p.id} className="flex items-center gap-2.5 px-3 py-2">
            <span className="relative"><Avatar name={p.name} id={p.id} size={32} /><Dot online={p.online} /></span>
            <span className="min-w-0">
              <span className="block truncate text-[13px] font-medium" style={{ color: 'var(--n-ink)' }}>{p.name}</span>
              <span className="block text-[11px]" style={{ color: p.online ? 'var(--n-success)' : 'var(--n-muted)' }}>{p.online ? 'online' : `last seen ${p.seen}`}</span>
            </span>
          </div>
        ))}
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="border-b p-2" style={{ borderColor: 'var(--n-line)' }}><div className="flex items-center gap-2 rounded-lg border px-2.5 py-1.5" style={{ borderColor: 'var(--n-border)' }}><Search size={14} style={{ color: 'var(--n-muted-2)' }} /><span className="text-[12px]" style={{ color: 'var(--n-muted-2)' }}>Search chats…</span></div></div>
        <div className="max-h-[42%] overflow-y-auto py-1">{CONVS.map((c) => <ConvRow key={c.id} c={c} />)}</div>
        <div className="min-h-0 flex-1 border-t" style={{ borderColor: 'var(--n-line)' }}><Thread /></div>
      </div>
    </div>
  </div>
);

// ---- Option C: Thread-first (avatar rail) ----------------------------------
const OptionC = () => (
  <div className="flex h-full">
    <div className="flex w-[68px] shrink-0 flex-col items-center gap-1 border-r py-3" style={{ borderColor: 'var(--n-line)', background: 'var(--n-surface)' }}>
      {CONVS.map((c) => (
        <div key={c.id} className="relative grid place-items-center rounded-xl p-1.5" style={{ background: c.active ? 'var(--n-accent-bg)' : 'transparent' }}>
          <span className="relative"><Avatar name={c.name} id={c.id} group={c.group} size={40} /><Dot online={c.online} /></span>
          {c.unread > 0 && <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[10px] font-bold text-white" style={{ background: 'var(--n-accent)' }}>{c.unread}</span>}
          {c.muted && <VolumeX size={11} className="absolute -bottom-0.5 -left-0.5" style={{ color: 'var(--n-muted-2)' }} />}
        </div>
      ))}
      <span className="mt-1 grid h-10 w-10 place-items-center rounded-xl border" style={{ borderColor: 'var(--n-border)', color: 'var(--n-accent)' }}><Plus size={18} /></span>
    </div>
    <div className="flex min-w-0 flex-1 flex-col"><Thread /></div>
  </div>
);

const OPTION = { a: OptionA, b: OptionB, c: OptionC };
const PANEL_W = { a: 470, b: 560, c: 500 };

// ---- faux app shell (desktop) ---------------------------------------------
const DesktopFrame = ({ opt }) => {
  const Panel = OPTION[opt] || OptionA;
  return (
    <div className="flex h-screen w-full overflow-hidden" style={{ background: 'var(--n-canvas)' }}>
      {/* left icon rail */}
      <aside className="flex w-16 shrink-0 flex-col items-center gap-3 border-r py-4" style={{ borderColor: 'var(--n-line)', background: 'var(--n-surface)' }}>
        <span className="grid h-9 w-9 place-items-center rounded-xl text-white" style={{ background: 'var(--n-accent)' }}><Feather size={18} /></span>
        <IconBtn><NotebookPen size={20} /></IconBtn>
        <IconBtn><ListChecks size={20} /></IconBtn>
        <span className="relative"><IconBtn active><MessageSquare size={20} /></IconBtn><span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[9px] font-bold text-white" style={{ background: 'var(--n-danger)' }}>4</span></span>
      </aside>
      {/* dim main content */}
      <main className="flex min-w-0 flex-1 flex-col opacity-50" style={{ background: 'var(--n-canvas)' }}>
        <div className="h-14 border-b" style={{ borderColor: 'var(--n-line)', background: 'var(--n-surface)' }} />
        <div className="flex-1 p-8">
          <div className="mb-4 h-8 w-56 rounded-lg" style={{ background: 'var(--n-canvas-2)' }} />
          <div className="grid grid-cols-3 gap-4">
            {[0,1,2,3,4,5].map((i) => <div key={i} className="h-28 rounded-xl border" style={{ borderColor: 'var(--n-line)', background: 'var(--n-surface)' }} />)}
          </div>
        </div>
      </main>
      {/* resize handle + chat panel */}
      <div className="w-1 cursor-col-resize" style={{ background: 'var(--n-accent-soft)' }} />
      <section className="flex shrink-0 flex-col" style={{ width: PANEL_W[opt] || 470, background: 'var(--n-surface)', boxShadow: 'var(--n-shadow)' }}>
        <Panel />
      </section>
    </div>
  );
};

// ---- mobile mockups --------------------------------------------------------
const MobileHome = () => (
  <div className="flex h-screen flex-col" style={{ background: 'var(--n-canvas)' }}>
    <div className="flex items-center gap-2 px-4 pb-2 pt-4">
      <span className="font-display text-[20px] font-bold" style={{ color: 'var(--n-ink)' }}>Chat</span>
      <span className="ml-auto flex items-center gap-1"><IconBtn><Search size={18} /></IconBtn><IconBtn><Users size={18} /></IconBtn></span>
    </div>
    <div className="flex gap-1 px-4 pb-1">
      <span className="rounded-lg px-3 py-1.5 text-[13px] font-semibold" style={{ background: 'var(--n-accent-bg)', color: 'var(--n-accent)' }}>Chats</span>
      <span className="rounded-lg px-3 py-1.5 text-[13px] font-medium" style={{ color: 'var(--n-muted)' }}>People</span>
    </div>
    <div className="flex-1 overflow-y-auto px-2 pt-1">{CONVS.map((c) => <ConvRow key={c.id} c={c} />)}</div>
    <span className="absolute bottom-20 right-5 grid h-14 w-14 place-items-center rounded-full text-white shadow-lg" style={{ background: 'var(--n-accent)' }}><Plus size={24} /></span>
    <nav className="flex items-stretch border-t" style={{ borderColor: 'var(--n-line)', background: 'var(--n-surface)' }}>
      {[['Notes', NotebookPen, false], ['Tasks', ListChecks, false], ['Chat', MessageSquare, true]].map(([label, Icon, on]) => (
        <div key={label} className="flex flex-1 flex-col items-center gap-0.5 py-2.5" style={{ color: on ? 'var(--n-accent)' : 'var(--n-muted)' }}>
          <Icon size={20} /><span className="text-[10.5px] font-semibold">{label}</span>
        </div>
      ))}
    </nav>
  </div>
);
const MobileThread = () => (
  <div className="flex h-screen flex-col" style={{ background: 'var(--n-canvas)' }}>
    <div className="flex items-center gap-2.5 border-b px-3 py-3" style={{ borderColor: 'var(--n-line)', background: 'var(--n-surface)' }}>
      <ArrowLeft size={20} style={{ color: 'var(--n-muted)' }} />
      <span className="relative"><Avatar name="Alice Kapoor" id="1" size={34} /><Dot online /></span>
      <span className="min-w-0"><span className="block truncate text-[15px] font-semibold" style={{ color: 'var(--n-ink)' }}>Alice Kapoor</span><span className="block text-[11px]" style={{ color: 'var(--n-success)' }}>online</span></span>
      <span className="ml-auto flex items-center"><IconBtn><Search size={18} /></IconBtn><IconBtn><MoreVertical size={18} /></IconBtn></span>
    </div>
    <div className="flex flex-1 flex-col gap-2.5 overflow-y-auto p-3">{MSGS.map((m) => <Bubble key={m.id} m={m} />)}</div>
    <Composer />
  </div>
);

export default function ChatLayoutPreview() {
  const { opt, theme, view } = useQuery();
  return (
    <div className={`notes-scope ${theme === 'dark' ? 'dark' : ''}`} style={{ minHeight: '100vh', background: 'var(--n-canvas)' }}>
      {view === 'mobile'
        ? <div className="mx-auto" style={{ maxWidth: 402 }}>{opt === 'thread' ? <MobileThread /> : <MobileHome />}</div>
        : <DesktopFrame opt={opt} />}
    </div>
  );
}
